import React, { useCallback, useMemo, useState } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { InputRadioArrowTable } from '../../../../../components/display/tables/InputRadioArrowTable';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Types
import { dataTypes } from '../../../../../components/display/tables/InputRadioArrowTable/types';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

// GQL
import { client } from '../../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../../gql/queries/communication';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../state/configureStore';

// Constants
import { formNaming as formCommunicationNaming } from '../../../../../components/forms/NewCommunicationForm/constants';

// Utils
import { compressSessionStorage } from '../../../../../utils/sessionStorage';

// Hooks
import { usePollingRevalidation } from '../../../../../hooks/usePollingRevalidation';
import { useAuth } from '../../../../../hooks/useAuth';

// RBAC
import { Role } from '../../../../../types/auth';
import { canEditOrderRow } from '../../../../../utils/rolePermissions';

// Página global de Comunicaciones, parametrizable para reutilizarse como
// pestaña de un LISTADO de módulo. Todos los props son opcionales y sus
// defaults reproducen el comportamiento global byte-idéntico.
//
// `containerTypes` filtra por TIPO de origen (pestaña "Comunicacions" del
// listado de Campañas), mismo criterio que `ActionsPage`/`MultimediaPage`.
//
// `containerId` (filtro por un container CONCRETO) se quedó SIN consumidores
// al retirarse las pestañas del detalle de campaña: hoy los llamadores — el
// listado de Campañas y el del módulo RV — usan `containerTypes`. Se conserva
// la parametrización, que es lo que hace reutilizable esta página.
export type ContextualListProps = {
  containerId?: string;
  containerTypes?: string[];
  tableId?: string;
  origin?: string;
  returnTo?: string;
};

export const CommunicationsPage = ({
  containerId,
  containerTypes,
  tableId = tableIds.WORKORDER_COMMUNICATIONS,
  origin = 'communications-table',
  returnTo,
}: ContextualListProps = {}) => {
  const { t } = useTranslation(['communications', 'pages']);
  const data = useAsyncValue() as dataTypes;
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // Gating rol × estado del botón "Editar comunicación" (2026-08-04). La tabla
  // solo devuelve el id por `setRowState`, así que el estado se busca en la
  // fila. `canEditOrderRow` es la MISMA regla del lápiz canónico de ShowOT:
  // nada de editar en `end`/`annulled`, y fuera de `open` solo los roles
  // privilegiados. Antes el botón (que vivía en Informes) solo miraba que
  // hubiera una fila seleccionada, así que dejaba entrar al formulario de una
  // comunicación anulada para encontrarse el Guardar bloqueado dentro.
  const canEditSelectedCommunication = useMemo(() => {
    if (!selectedRowId) return false;
    const row = (data?.ordersWithDataCommunication || []).find(
      (item) => item.id === selectedRowId
    );
    return canEditOrderRow(currentRole, row?.state);
  }, [selectedRowId, data?.ordersWithDataCommunication, currentRole]);

  const handleEditCommunication = useCallback(() => {
    if (!selectedRowId) return;

    sessionStorage.removeItem(formCommunicationNaming.EDIT_COMMUNICATION_FORM);
    sessionStorage.removeItem(
      formCommunicationNaming.EDIT_COMMUNICATION_FORM_OT
    );
    sessionStorage.removeItem(
      formCommunicationNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN
    );

    // Se escribe SIEMPRE, y a propósito: si el usuario abrió antes esta misma
    // comunicación desde el detalle de una OT, `ShowOT/CommunicationOT` dejó en
    // sesión `{origin:'workorder', parentOrderId…}` y al guardar lo mandaríamos
    // a una OT que no tiene nada que ver con esta edición. `AccidentsPage` hace
    // lo mismo por el mismo motivo.
    sessionStorage.setItem(
      formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          // Order de la comunicación que se abre (ver `communicationEditDraft`).
          entityId: selectedRowId,
          origin,
          ...(returnTo && { returnTo }),
        })
      )
    );

    navigate(`/work-orders/communications/edit-communication/${selectedRowId}`);
  }, [selectedRowId, origin, returnTo, navigate]);

  // Abre la comunicación `id`. Lo usan «Ver comunicación» (con la fila
  // seleccionada) y el doble toque de la tabla.
  //
  // Escribe el contexto SIEMPRE, también en la pantalla global (antes no lo
  // hacía): sin él, el resumen heredaba el de la última visita —p. ej.
  // `{origin:'workorder', parentOrderId}` de abrirla desde una OT— y al salir
  // mandaba a esa OT. En la pantalla global el valor es el mismo que el resumen
  // ya tomaba por defecto, así que el destino no cambia (spec
  // 2026-10-06-return-to-origin § 4 E2). «Editar» ya lo hacía por lo mismo.
  const openCommunication = useCallback((id: string) => {
    sessionStorage.setItem(
      formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          origin,
          ...(returnTo && { returnTo }),
        })
      )
    );
    navigate(`/work-orders/communications/${id}`);
  }, [origin, returnTo, navigate]);

  const handleSeeCommunication = useCallback(
    () => openCommunication(selectedRowId),
    [openCommunication, selectedRowId],
  );

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableId)(store.getState());
    // `containerTypes` prop (estático, pestaña "Comunicacions" del listado
    // de Campañas) gana sobre el cursor; en la tabla GLOBAL el prop está
    // vacío y el valor sale del filtro "Origen" user-driven persistido en
    // el cursor.
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const communications = await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          roadId: currentCursor.roadId,
          concessionId: currentCursor.concessionId,
          interlocutorGroupId: currentCursor.interlocutorGroupId,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          teamIds: currentCursor.teamIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          predefinedOrderId: currentCursor.predefinedOrderId,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
          // GOTCHA (review T1): omitir en vez de null/undefined explícito —
          // `containerId: null` SÍ entra en la keyArgs de Apollo y partiría
          // la caché histórica sin containerId (ver policies.ts). Misma
          // regla para `containerTypes`.
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      roadId: currentCursor?.roadId,
      concessionId: currentCursor?.concessionId,
      interlocutorGroupId: currentCursor?.interlocutorGroupId,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      states: currentCursor?.states,
      userIds: currentCursor?.userIds,
      teamIds: currentCursor?.teamIds,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      onlyFlagged: currentCursor?.onlyFlagged,
      // Redux (no Apollo): full-replace de la slice del cursor — se escribe
      // el containerId/containerTypes del prop para no perderlos en el
      // replace; undefined en el path global.
      containerId,
      containerTypes: effectiveContainerTypes,
    };

    dispatch(setCursor(tableId, setCursorData));
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          roadId: currentCursor.roadId,
          concessionId: currentCursor.concessionId,
          interlocutorGroupId: currentCursor.interlocutorGroupId,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          teamIds: currentCursor.teamIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          predefinedOrderId: currentCursor.predefinedOrderId,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor.onlyFlagged,
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);
  usePollingRevalidation(onPollingRefresh);
  return (
    <Container>
      <InputRadioArrowTable
        titleText={t('filterBy')}
        data={data}
        setRowState={setSelectedRowId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableId}
        onRowOpen={openCommunication}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas»
        // (Comunicaciones y la pestaña de comunicaciones del listado de
        // Campañas). Duplicar comunicación no los pasa.
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        {/* "Editar comunicación" (2026-08-04): vive aquí, no en Informes —
            mismo patrón que Accidentes. El gating es rol × estado; los roles
            son espejo del `RolesAuthRoute` de la ruta destino (SuperAdmin,
            Coex, Capataz, RoomTeam), que `canEditOrderRow` ya aplica. */}
        <MainButton
          variant="secondary"
          text={t('edit')}
          onClick={handleEditCommunication}
          disabled={!canEditSelectedCommunication}
        />
        <MainButton
          text={t('view')}
          onClick={handleSeeCommunication}
          disabled={selectedRowId.length === 0}
        />
      </ContainerButton>
    </Container>
  );
};
